// Module 10.6 — point assets (§3) + power plants (§2.1). Assets render one glyph per node (shape by
// class, unlit when in the outage set). Plants join dispatch → generators by id: lit when producing,
// fill = output/cap, height log-scaled by capMw (log10 is a DISPLAY transform — allowed in viz, never
// in the solver). Generators absent (v1-shaped model) ⇒ no plant columns, NOT an error.
import type { GridNode, GridEdge, Generator, Dispatch, BranchFlow } from "../../domain/index";
import { loadBand } from "../../domain/loading-bands";
import type { AssetSpec, AssetStatus, PlantSpec } from "./spec";
import { NODE_SHAPE, FUEL_COLOR, FUEL_ICON } from "./palette";

// Distinct identity hues per node class so power plants, substations, transformers, and junctions
// are visually distinguishable on the map (not all the same grey). Power-plant buses get a warm gold;
// substations get a vivid magenta (NOT blue — FUEL_COLOR's NUCLEAR #332288 / HYDRO #0072B2 plant
// columns are both blue-family, so a blue substation marker read as "the same thing" next to a
// nuclear/hydro plant; magenta has zero overlap with the fuel palette); transformers get purple;
// switches get teal; junctions stay neutral grey.
const ASSET_COLOR_BY_CLASS: Record<string, string> = {
  generator: "#FFB300",   // warm amber — "this is a power plant bus"
  substation: "#D81B60",  // vivid magenta — "this is a substation" (distinct from any fuel hue)
  transformer: "#7B1FA2", // purple — transformers are distinct from substations
  switch: "#00838F",      // teal — switchgear
  junction: "#9E9E9E",    // neutral grey — passive junction
  consumer: "#78909C",    // blue-grey
  prosumer: "#00897B",    // teal-green
};
const ASSET_COLOR = "#cfd8dc"; // legacy fallback (unused now but kept for the test fake)

// P16 C44: the node-point/parcel loading ladder now reads the ONE shared `loadBand` from domain/loading-bands.ts
// (55/80/100) — the SAME source the engine's wall status uses. De-energized (in the outage set) → "outage";
// else the shared ladder. This is a behaviour-parity relocation (55/80/100 unchanged).
function incidentLoadByBus(branchFlows: readonly BranchFlow[], edgeById: ReadonlyMap<string, GridEdge>): Map<string, number> {
  const worst = new Map<string, number>();
  for (const bf of branchFlows) {
    const e = edgeById.get(bf.edgeId as string);
    if (!e) continue;
    const pct = bf.loadingPct;
    const u = e.u as string;
    const v = e.v as string;
    if (pct > (worst.get(u) ?? -1)) worst.set(u, pct);
    if (pct > (worst.get(v) ?? -1)) worst.set(v, pct);
  }
  return worst;
}
function assetStatus(id: string, outageSet: ReadonlySet<string>, worstIncident: ReadonlyMap<string, number>): AssetStatus {
  if (outageSet.has(id)) return "outage";
  return loadBand(worstIncident.get(id) ?? 0);
}

export function encodeAssets(
  nodes: readonly GridNode[],
  outageSet: ReadonlySet<string>,
  branchFlows: readonly BranchFlow[] = [],
  edgeById?: ReadonlyMap<string, GridEdge>,
): AssetSpec[] {
  const worstIncident = edgeById ? incidentLoadByBus(branchFlows, edgeById) : new Map<string, number>();
  return nodes.map((n) => {
    const id = n.id as string;
    return {
      id,
      cls: n.cls,
      coord: n.coord,
      shape: NODE_SHAPE[n.cls],
      color: ASSET_COLOR_BY_CLASS[n.cls] ?? ASSET_COLOR,
      lit: !outageSet.has(id),
      status: assetStatus(id, outageSet, worstIncident),
    };
  });
}

// Sized to read at the boot camera (~150 km viewport): 40+120·log10 put Darlington at ~465 m —
// subpixel from full-GTA zoom, so plant towers looked absent. Darlington now ≈ 1.3 km. Display only.
const BASE_HEIGHT_M = 150;
const HEIGHT_SCALE_M = 320;

export function encodePlants(dispatch: readonly Dispatch[], generators: readonly Generator[] | undefined): PlantSpec[] {
  if (!generators || generators.length === 0) return [];
  const byId = new Map<string, Generator>();
  for (const g of generators) byId.set(g.id as string, g);
  const out: PlantSpec[] = [];
  for (const d of dispatch) {
    const g = byId.get(d.genId as string);
    if (!g) continue; // dispatch entry with no known generator → skip, not an error
    const cap = g.capMw > 0 ? g.capMw : 1;
    out.push({
      id: g.id as string,
      busId: g.busId as string,
      coord: g.coord,
      heightM: BASE_HEIGHT_M + HEIGHT_SCALE_M * Math.log10(1 + g.capMw),
      fillFrac: Math.max(0, Math.min(1, d.outputMw / cap)),
      lit: d.outputMw > 0,
      fuel: g.fuel,
      color: FUEL_COLOR[g.fuel],
      icon: FUEL_ICON[g.fuel],
      status: g.status ?? "Operating", // 4.10: drives the faint-grey retired-plant styling
    });
  }
  return out;
}
